import React, { createContext, useContext, useEffect, useState } from 'react'

// Define LoginUser interface here to avoid circular dependency
export interface LoginUser {
  id: string
  userId: string
  referralCode: string
  role: string
  balance: number
}

// Secure storage utilities
const storage = {
  setItem: (key: string, value: string) => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(key, value)
      }
    } catch (error) {
      console.error('Error storing data:', error)
    }
  },
  
  getItem: (key: string): string | null => {
    try {
      if (typeof window !== 'undefined') {
        return localStorage.getItem(key)
      }
      return null
    } catch (error) {
      console.error('Error retrieving data:', error)
      return null
    }
  },
  
  removeItem: (key: string) => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(key)
      }
    } catch (error) {
      console.error('Error removing data:', error)
    }
  }
}

// Auth utilities
export const auth = {
  // Store authentication data
  login: (token: string, user: LoginUser) => {
    storage.setItem('auth_token', token)
    storage.setItem('user_data', JSON.stringify(user))
  },
  
  // Get authentication data
  getToken: (): string | null => {
    return storage.getItem('auth_token')
  },
  
  getUser: (): LoginUser | null => {
    const userData = storage.getItem('user_data')
    if (userData) {
      try {
        return JSON.parse(userData)
      } catch (error) {
        console.error('Error parsing user data:', error)
        return null
      }
    }
    return null
  },
  
  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    return !!auth.getToken()
  },
  
  // Check if user is admin
  isAdmin: (): boolean => {
    const user = auth.getUser()
    return user?.role === 'ADMIN'
  },
  
  // Logout
  logout: () => {
    storage.removeItem('auth_token')
    storage.removeItem('user_data')
  }
}

interface AuthContextType {
  user: LoginUser | null
  token: string | null
  isAuthenticated: boolean
  isAdmin: boolean
  login: (token: string, user: LoginUser) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<LoginUser | null>(null)
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    // Initialize auth state from storage
    const storedToken = auth.getToken()
    const storedUser = auth.getUser()
    
    if (storedToken && storedUser) {
      setToken(storedToken)
      setUser(storedUser)
    }
  }, [])

  const login = (newToken: string, newUser: LoginUser) => {
    auth.login(newToken, newUser)
    setToken(newToken)
    setUser(newUser)
  }

 // In your AuthContext
const logout = () => {
  // Remove tokens from localStorage
  localStorage.removeItem('auth_token')
  localStorage.removeItem('user_data')
  
}

  const value = {
    user,
    token,
    isAuthenticated: !!token,
    isAdmin: user?.role === 'ADMIN',
    login,
    logout,
  }

  const Provider = AuthContext.Provider

  return React.createElement(Provider, { value }, children)
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}